import { Component, Inject, NgModule } from '@angular/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Reason, ReasonLabels } from '../../../interfaces/crossmatch';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { Recipient } from '../../../interfaces/recipient';
import { Donor } from '../../../interfaces/donor';
import { CrossmatchService } from '../../../services/crossmatch.service';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { PairService } from '../../../services/pair.service';
import { ParticipantService } from '../../../services/participant.service';
import { Pair } from '../../../interfaces/pair';
import { MatIconModule } from '@angular/material/icon';
import { NotifyService } from '../../../common/notify/notify.service';

@Component({
  selector: 'app-add-excluded',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatIconModule,
    MatSelectModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './add-excluded.component.html',
  styleUrl: './add-excluded.component.scss',
  providers: [NotifyService]
})
export class AddExcludedComponent {
  donorName?: string;
  recipientName?: string;
  reasonExcluded?: Reason;
  matchrunForm!: FormGroup;
  donorSearchControl = new FormControl(''); // Separate control for search
  recipientSearchControl = new FormControl('');

  reasons = Object.keys(ReasonLabels) as Reason[]; // Get enum keys
  reasonsLabels = ReasonLabels; // Label mapping

  pairs: Pair[] = [];
  donors: Donor[] = [];
  recipients: Recipient[] = [];
  filteredDonors = this.donors;
  filteredRecipients = this.recipients;
  selectedDonor: any = null;
  selectedRecipient: any = null;
  isFromMatchrun: boolean = false;

  constructor(
    private readonly dialogRef: MatDialogRef<AddExcludedComponent>,
    private readonly crossmatchService: CrossmatchService,
    private fb: FormBuilder,
    private pairService: PairService,
    private participantService: ParticipantService,
    private readonly notify: NotifyService,
    private readonly crossMatchService: CrossmatchService,
    @Inject(MAT_DIALOG_DATA) public data?: AddExcludedDialogData
  ) {
    this.matchrunForm = this.fb.group({
      donor: [null, Validators.required],
      recipient: [null, Validators.required],
      reasonExcluded: ['', Validators.required],
      excluded: [true],
      comments: ['']
    });

    this.loadPairsAndParticipants();

    // Handle preselected participants
    if (data?.preselectedRecipient && data?.preselectedDonor) {
      this.isFromMatchrun = true;
      this.selectedRecipient = data.preselectedRecipient;
      this.recipientSearchControl.setValue(this.displayRecipientName(data.preselectedRecipient));
      this.recipientSearchControl.disable();
      this.matchrunForm.get('recipient')?.setValue(data.preselectedRecipient.id);

      this.selectedDonor = data.preselectedDonor;
      this.donorSearchControl.setValue(this.displayDonorName(data.preselectedDonor));
      this.donorSearchControl.disable();
      this.matchrunForm.get('donor')?.setValue(data.preselectedDonor.id);
    }
  }

  async addExcludedCombination() {
    if (this.matchrunForm.valid) {
      const matchrunData = this.matchrunForm.value;
      console.log('Submitting excluded combination data:', matchrunData);

      this.crossMatchService.createCrossmatch(matchrunData).subscribe({
        next: (response) => {
          console.log('Combination created:', response);
          this.notify.withAutodismiss(`Combination created successfully!`);
          this.dialogRef.close(response);
        },
        error: (err) => {
          console.error('Error creating Combination:', err);
          this.notify.withAutodismiss('Failed to create Combination. Please try again.');
        }
      });
    } else {
      console.log('Form invalid:', this.matchrunForm.errors);
      console.log('Form values:', this.matchrunForm.value);
      this.notify.withAutodismiss('Please fill out all required fields correctly.');
    }
  }

  private async loadPairsAndParticipants(): Promise<void> {
    // Load pairs first
    await this.loadPairs();

    // Then load and filter participants
    this.loadParticipants();
  }

  private async loadPairs(): Promise<void> {
    return new Promise((resolve) => {
      this.pairService.getAllPairs().subscribe((pairs) => {
        this.pairs = pairs;
        resolve();
      });
    });
  }

  private loadParticipants(): void {
    this.participantService.getAllParticipants().subscribe((participants) => {
      // Filter participants based on pairings
      participants.forEach((participant) => {
        if (participant.type === 'Donor') {
          this.donors.push(participant as Donor);
        } else if (participant.type === 'Recipient') {
          this.recipients.push(participant as Recipient);
        }
      });
    });
  }
  filterDonors() {
    const searchValue = this.donorSearchControl.value!.toLowerCase();
    this.filteredDonors = this.donors.filter((donor) =>
      `${donor.enisNumber} ${donor.firstName} ${donor.lastName}`.toLowerCase().includes(searchValue)
    );
  }

  filterRecipients() {
    const searchValue = this.recipientSearchControl.value!.toLowerCase();
    this.filteredRecipients = this.recipients.filter((recipient) =>
      `${recipient.enisNumber} ${recipient.firstName} ${recipient.lastName}`.toLowerCase().includes(searchValue)
    );
  }

  displayDonorName = (donor: Donor | string | null): string => {
    if (typeof donor === 'string') {
      return donor;
    }
    if (!donor) {
      return '';
    }
    const firstName = donor.firstName || '';
    const lastName = donor.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName ? `${donor.enisNumber} - ${fullName}` : donor.enisNumber || '';
  }

  displayRecipientName = (recipient: Recipient | string | null): string => {
    if (typeof recipient === 'string') {
      return recipient;
    }
    if (!recipient) {
      return '';
    }
    const firstName = recipient.firstName || '';
    const lastName = recipient.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName ? `${recipient.enisNumber} - ${fullName}` : recipient.enisNumber || '';
  }

  selectDonor(donor: Donor) {
    if (donor && this.selectedDonor?.id !== donor.id) {
      this.selectedDonor = donor;
      this.matchrunForm.get('donor')?.setValue(donor.id);
      this.donorSearchControl.setValue(this.displayDonorName(donor), { emitEvent: false });
    }
  }

  selectRecipient(recipient: Recipient) {
    if (recipient && this.selectedRecipient?.id !== recipient.id) {
      this.selectedRecipient = recipient;
      this.matchrunForm.get('recipient')?.setValue(recipient.id);
      this.recipientSearchControl.setValue(this.displayRecipientName(recipient), { emitEvent: false });
    }
  }

  clearDonorSelection(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedDonor = null;
    this.matchrunForm.get('donor')?.reset();
    this.filteredDonors = this.donors;
    // Reset the search control completely
    this.donorSearchControl.reset('');
  }

  clearRecipientSelection(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedRecipient = null;
    this.matchrunForm.get('recipient')?.reset();
    this.filteredRecipients = this.recipients;
    // Reset the search control completely
    this.recipientSearchControl.reset('');
  }
}
export interface AddExcludedDialogData {
  donorId?: string;
  recipientId?: string;
  preselectedRecipient?: any;
  preselectedDonor?: any;
}

export interface AddExcludedDialogResult {
  donorId: string;
  recipientId: string;
}
